// What every funnel state PUTS ON SCREEN — title, lead copy, and the one control
// that leaves it. One table, walked by `onboardingSteps.test.ts` and by
// `OnboardingPanel.states.test.tsx`, because a hand-kept list is how this app
// shipped a card nobody could leave. Twice:
//
//  - `new` had no `<Match>` at all, so it fell through the panel's `fallback`
//    and told a signed-in user "Signing you in…" with no button and no poll.
//    Four prod accounts sat there on 2026-08-25.
//  - `yml_requested` was excluded from the footer list, so the step that waits
//    on a human pushing a file to a default branch — days, sometimes — offered
//    a spinner and nothing else.
//
// `OnboardingState` is a CLOSED enum, so `Record<OnboardingState, …>` makes a
// new state a compile error here instead of a `fallback` in production. Same
// seam, same reason, as the notification-kind and run-event-kind label tables
// in `i18n.test.ts`.

import { OnboardingEvent, OnboardingState } from '@developerz/domain';

export type OnboardingStepSpec = {
  /** Card heading. */
  readonly titleKey: string;
  /** The lead sentence — what this step is and what happens next. */
  readonly bodyKey: string;
  /** The card's PRIMARY control. Never absent: a step with nothing to click is a dead end. */
  readonly actionKey: string;
  /**
   * The transition that control fires, or `null` when it is not an advance —
   * a link out (the keys page, the GitHub App install screen) or a re-read of
   * `/v1/onboarding/state`. Non-null entries are asserted against the domain's
   * own `nextFunnelMove`/`advance`, so the panel can never fire a transition the
   * server would only ever answer 409 to.
   */
  readonly advanceEvent: OnboardingEvent | null;
};

export const ONBOARDING_STEPS: Readonly<Record<OnboardingState, OnboardingStepSpec>> = {
  [OnboardingState.New]: {
    titleKey: 'onboarding.step_auth',
    bodyKey: 'onboarding.auth_body',
    actionKey: 'onboarding.auth_cta',
    advanceEvent: OnboardingEvent.AuthCompleted,
  },
  [OnboardingState.Authed]: {
    titleKey: 'onboarding.step_plan',
    bodyKey: 'onboarding.plan_body',
    actionKey: 'onboarding.plan_team_cta',
    advanceEvent: OnboardingEvent.PlanSelected,
  },
  [OnboardingState.PlanSelected]: {
    titleKey: 'onboarding.step_keys',
    bodyKey: 'onboarding.keys_body',
    actionKey: 'onboarding.keys_cta',
    // The key is captured and VERIFIED on the keys page; the next funnel settle
    // then fires `key_set` (#4613). This card links out; it never advances.
    advanceEvent: null,
  },
  [OnboardingState.KeySet]: {
    titleKey: 'onboarding.step_connect',
    bodyKey: 'onboarding.connect_body',
    actionKey: 'onboarding.connect_cta',
    // GitHub's install webhook fires `gh_app_installed`. Nothing to advance here.
    advanceEvent: null,
  },
  [OnboardingState.GhConnected]: {
    // Epic #3629: the funnel used to end with no machine to run on — every
    // account that clicked through landed on a done card over a blocked
    // `scout_box` rung. The card mounts the two enrollment transports and the
    // live roster; the API's fact ladder settles `box_attached` off the first
    // heartbeat. The control here re-READS state; nothing self-declares a box.
    titleKey: 'onboarding.step_box',
    bodyKey: 'onboarding.box_body',
    actionKey: 'onboarding.box_cta',
    advanceEvent: null,
  },
  [OnboardingState.BoxAttached]: {
    titleKey: 'onboarding.step_repos',
    bodyKey: 'onboarding.repos_body',
    actionKey: 'onboarding.repos_continue',
    advanceEvent: OnboardingEvent.ReposSelected,
  },
  [OnboardingState.ReposSelected]: {
    titleKey: 'onboarding.step_yml',
    // arc 101 slice 05 step 6: this step's CONTENT is the pass's pull request, not
    // a prompt to paste. The repo-onboarding pass is minted on first enable and
    // PROPOSES `.maintainer.yml` on `dz/onboarding`; the card used to tell the
    // maintainer to write that same file by hand and neither half knew about the
    // other. Merging the PR now completes the funnel from HERE
    // (`ymlVerdictEvents` admits this state), so the copy can point at it honestly.
    // The hand-written path stays, demoted to the fallback it always was.
    bodyKey: 'onboarding.yml_pr_body',
    actionKey: 'onboarding.yml_pr_continue',
    advanceEvent: OnboardingEvent.YmlRequested,
  },
  [OnboardingState.YmlRequested]: {
    // Epic #3629: the wait on the setup PR is days, so the chat-client hookup
    // (the MCP address, `<ConnectMcpPanel/>`) fills it. The fact ladder settles
    // `mcp_connected` off the first connector grant / PAT; the yml verdict keeps
    // landing in the background and the card keeps the snippets on screen.
    titleKey: 'onboarding.step_mcp',
    bodyKey: 'onboarding.mcp_body',
    actionKey: 'onboarding.mcp_cta',
    advanceEvent: null,
  },
  [OnboardingState.McpConnected]: {
    titleKey: 'onboarding.step_validate',
    bodyKey: 'onboarding.validate_body',
    actionKey: 'onboarding.validate_check_now',
    // The policy-ingest consumer fires `yml_validated`/`yml_invalid` off the
    // repo's default branch — which is what merging `dz/onboarding` produces.
    // The control here re-READS state; it never asserts one.
    advanceEvent: null,
  },
  [OnboardingState.Invalid]: {
    titleKey: 'onboarding.step_validate',
    bodyKey: 'onboarding.validate_invalid',
    actionKey: 'onboarding.retry',
    advanceEvent: OnboardingEvent.YmlRetried,
  },
  [OnboardingState.Active]: {
    titleKey: 'onboarding.done_heading',
    bodyKey: 'onboarding.done_body',
    actionKey: 'onboarding.done_cta',
    // Terminal — onboarding is over forever (`onboarding.md` § Steady state).
    advanceEvent: null,
  },
};
